import { BaseFieldRenderer } from './base-field-renderer';
import { HiddenFieldJsonData, RenderOptions } from '../../models/element-types';
import { DomUtils } from '../../utils/dom-utils';
import { TranslateUtils } from '../../utils/translate.utils';
import { Base64Utils } from '../../utils/base-64-utils';
import { HiddenFieldTags } from '../../models/hidden/hidden-block.model';

export class HiddenFieldRenderer extends BaseFieldRenderer<HiddenFieldJsonData> {
    protected override get defaultData(): HiddenFieldJsonData {
        return {
            id: '',
            type: 'hidden',
            value: '',
            dataType: 'text',
            label: ''
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected override renderElement(fieldJsonData: HiddenFieldJsonData, options: RenderOptions = {}): HTMLElement {
        const wrapper = document.createElement('div');
        wrapper.id = fieldJsonData.id;
        wrapper.setAttribute('data-group', 'hiddenFields');
        wrapper.setAttribute('data-element', 'hidden');
        if (fieldJsonData.label) {
            wrapper.setAttribute('data-label', fieldJsonData.label);
        }
        if (fieldJsonData.crmFieldData) {
            wrapper.setAttribute('data-crm-field', Base64Utils.serialize(fieldJsonData.crmFieldData) ?? '');
        }

        const inputId = DomUtils.generateUniqueElementId([]);
        const input = document.createElement('input');
        input.id = inputId;
        input.name = fieldJsonData.id;
        input.type = 'hidden';
        input.value = fieldJsonData.value || '';

        input.setAttribute('data-type', fieldJsonData.dataType || 'text');

        wrapper.appendChild(input);

        wrapper.appendChild(this.createBuilderInfo(fieldJsonData));

        return wrapper;
    }

    protected override applyVisualProperties(element: HTMLElement, data: HiddenFieldJsonData): void {
        if (data.label) {
            element.setAttribute('data-label', data.label);
        } else {
            element.removeAttribute('data-label');
        }
        if (data.crmFieldData) {
            element.setAttribute('data-crm-field', Base64Utils.serialize(data.crmFieldData) ?? '');
        } else {
            element.removeAttribute('data-crm-field');
        }

        const input = element.querySelector<HTMLInputElement>('input[type="hidden"]');
        if (input) {
            input.value = data.value || '';
            input.setAttribute('data-type', data.dataType || 'text');
        }

        this.updateBuilderInfo(element, data);
    }

    private updateBuilderInfo(element: HTMLElement, fieldJsonData: HiddenFieldJsonData): void {
        let builderInfo = element.querySelector<HTMLElement>('.field-info');
        if (!builderInfo) {
            builderInfo = this.createBuilderInfo(fieldJsonData);
            element.appendChild(builderInfo);
            return;
        }

        const titleElement = builderInfo.querySelector<HTMLElement>('.field-title');
        if (titleElement) {
            titleElement.textContent = fieldJsonData.label || TranslateUtils.HIDDEN_FIELD.LABEL;
        }

        const typeElement = builderInfo.querySelector<HTMLElement>('.field-type');
        if (typeElement) {
            let typeText = TranslateUtils.HIDDEN_FIELD.TYPE_TEXT;
            switch (fieldJsonData.dataType) {
                case 'date':
                    typeText = TranslateUtils.HIDDEN_FIELD.TYPE_DATE;
                    break;
                case 'number':
                    typeText = TranslateUtils.HIDDEN_FIELD.TYPE_NUMBER;
                    break;
                case 'email':
                    typeText = TranslateUtils.HIDDEN_FIELD.TYPE_EMAIL;
                    break;
            }
            typeElement.textContent = `${typeText}. ${TranslateUtils.HIDDEN_FIELD.SAVE_TO}: ${fieldJsonData.crmFieldData?.name ?? '-'}`;
        }

        const valueElement = builderInfo.querySelector<HTMLElement>('.field-value');
        if (valueElement) {
            if (fieldJsonData.dataType === 'date' && fieldJsonData.value === HiddenFieldTags.SubmissionDate) {
                valueElement.textContent = `${TranslateUtils.HIDDEN_FIELD.VALUE_EQ}: ${TranslateUtils.HIDDEN_FIELD.SUBMISSION_DATE}`;
            } else {
                valueElement.textContent = `${TranslateUtils.HIDDEN_FIELD.VALUE_EQ}: ${fieldJsonData.value ?? ''}`;
            }
        }
    }

    private createBuilderInfo(fieldJsonData: HiddenFieldJsonData): HTMLElement {
        const builderInfo = document.createElement('div');
        builderInfo.className = 'element-builder-only field-info';

        const headline = document.createElement('div');
        headline.className = 'line field-headline';
        headline.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="field-icon">
                <g clip-path="url(#clip0_6602_30165)">
                <path d="M7.15625 3.38401C8.70917 3.19895 10.28 3.52723 11.6288 4.31871C12.9777 5.1102 14.0304 6.3214 14.6263 7.76735C14.6818 7.91703 14.6818 8.08167 14.6263 8.23135C14.3812 8.82535 14.0575 9.38368 13.6636 9.89135" stroke="#6B7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M9.38942 9.43866C9.01222 9.80297 8.50702 10.0046 7.98262 10C7.45823 9.99545 6.9566 9.78511 6.58579 9.41429C6.21497 9.04348 6.00463 8.54185 6.00008 8.01746C5.99552 7.49307 6.19711 6.98786 6.56142 6.61066" stroke="#6B7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M11.6517 11.666C10.7674 12.1899 9.78068 12.5174 8.75861 12.6263C7.73654 12.7352 6.703 12.623 5.72812 12.2973C4.75324 11.9716 3.85983 11.4399 3.10851 10.7385C2.35719 10.0371 1.76554 9.18226 1.3737 8.23202C1.31814 8.08235 1.31814 7.9177 1.3737 7.76802C1.96479 6.33459 3.00482 5.13152 4.3377 4.33936" stroke="#6B7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M1.33398 1.33331L14.6673 14.6666" stroke="#6B7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                <clipPath id="clip0_6602_30165">
                <rect width="16" height="16" fill="white"/>
                </clipPath>
                </defs>
            </svg>
            <span class="field-title">${fieldJsonData.label || TranslateUtils.HIDDEN_FIELD.LABEL}</span>
        `;
        builderInfo.appendChild(headline);

        const typeLine = document.createElement('div');
        typeLine.className = 'line field-type';
        let typeText = TranslateUtils.HIDDEN_FIELD.TYPE_TEXT;
        switch (fieldJsonData.dataType) {
            case 'date':
                typeText = TranslateUtils.HIDDEN_FIELD.TYPE_DATE;
                break;
            case 'number':
                typeText = TranslateUtils.HIDDEN_FIELD.TYPE_NUMBER;
                break;
            case 'email':
                typeText = TranslateUtils.HIDDEN_FIELD.TYPE_EMAIL;
                break;
        }
        typeLine.textContent = `${typeText}. ${TranslateUtils.HIDDEN_FIELD.SAVE_TO}: ${fieldJsonData.crmFieldData?.name ?? '-'}`;
        builderInfo.appendChild(typeLine);

        const valueLine = document.createElement('div');
        valueLine.className = 'line field-value';
        if (fieldJsonData.dataType === 'date' && fieldJsonData.value === HiddenFieldTags.SubmissionDate) {
            valueLine.textContent = `${TranslateUtils.HIDDEN_FIELD.VALUE_EQ}: ${TranslateUtils.HIDDEN_FIELD.SUBMISSION_DATE}`;
        } else {
            valueLine.textContent = `${TranslateUtils.HIDDEN_FIELD.VALUE_EQ}: ${fieldJsonData.value ?? ''}`;
        }
        builderInfo.appendChild(valueLine);

        return builderInfo;
    }

    protected override renderContent(): HTMLElement {
        return document.createElement('div');
    }

    protected renderInput(): HTMLElement {
        return document.createElement('div');
    }
}
